import { Card, Col, Radio, Row, Space, Switch, Tooltip, Typography } from 'antd';
import { GraySpan } from 'components/Custom/spans';
import { ProfileWrapper } from 'components/Custom/wrappers';
import * as React from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { PwaAccessibilityPage } from 'pwa/components/PwaAccessibilityPage';
import routes from 'routes';
import { getAuthUser } from 'store/auth/selectors';
import {
  A11Y_FOCUS_STRONG,
  A11Y_HIGH_CONTRAST,
  A11Y_REDUCE_MOTION,
  A11Y_TEXT_SCALE,
  A11Y_TEXT_SPACING,
  A11Y_UNDERLINE_LINKS,
  A11yPreferences,
  A11yReduceMotion,
  A11yTextScale,
  A11yTextSpacing,
  applyA11yPreferences,
  DEFAULT_A11Y_PREFERENCES,
  normalizeA11yPreferences,
} from 'utils/apply-a11y-preferences';
import {
  PreferenceActions,
  PreferenceSections,
  usePreferences,
} from 'utils/preferences';
import { usePwaMobileNavEnabled } from 'utils/usePwaMobileNavEnabled';
const { Text, Title } = Typography;

export const AccessibilityPage: React.FC = () => {
  const { t } = useTranslation();
  const pwaMobileNav = usePwaMobileNavEnabled();
  const currentUser = useSelector(getAuthUser);
  const preferences = usePreferences();
  const userUuid = currentUser?.userUuid ?? '';

  const readPrefs = (): A11yPreferences => {
    if (!userUuid) return DEFAULT_A11Y_PREFERENCES;
    return normalizeA11yPreferences({
      [A11Y_TEXT_SCALE]: preferences(
        PreferenceActions.GET,
        PreferenceSections.APP,
        { name: A11Y_TEXT_SCALE },
        userUuid
      ),
      [A11Y_TEXT_SPACING]: preferences(
        PreferenceActions.GET,
        PreferenceSections.APP,
        { name: A11Y_TEXT_SPACING },
        userUuid
      ),
      [A11Y_HIGH_CONTRAST]: preferences(
        PreferenceActions.GET,
        PreferenceSections.APP,
        { name: A11Y_HIGH_CONTRAST },
        userUuid
      ),
      [A11Y_UNDERLINE_LINKS]: preferences(
        PreferenceActions.GET,
        PreferenceSections.APP,
        { name: A11Y_UNDERLINE_LINKS },
        userUuid
      ),
      [A11Y_REDUCE_MOTION]: preferences(
        PreferenceActions.GET,
        PreferenceSections.APP,
        { name: A11Y_REDUCE_MOTION },
        userUuid
      ),
      [A11Y_FOCUS_STRONG]: preferences(
        PreferenceActions.GET,
        PreferenceSections.APP,
        { name: A11Y_FOCUS_STRONG },
        userUuid
      ),
    });
  };

  const [a11yPrefs, setA11yPrefs] = React.useState<A11yPreferences>(
    DEFAULT_A11Y_PREFERENCES
  );

  React.useEffect(() => {
    const next = readPrefs();
    setA11yPrefs(next);
    applyA11yPreferences(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- sync from persisted prefs only
  }, [userUuid, currentUser?.prefDetails?.[1]]);

  const savePreference = (name: string, value: unknown) => {
    if (!userUuid) return;
    preferences(
      PreferenceActions.SET,
      PreferenceSections.APP,
      { name, value },
      userUuid
    );
  };

  const updatePrefs = (partial: Partial<A11yPreferences>) => {
    const next = { ...a11yPrefs, ...partial };
    setA11yPrefs(next);
    applyA11yPreferences(next);
  };

  const onTextScaleChange = (value: A11yTextScale) => {
    updatePrefs({ textScale: value });
    savePreference(A11Y_TEXT_SCALE, value);
  };

  const onTextSpacingChange = (value: A11yTextSpacing) => {
    updatePrefs({ textSpacing: value });
    savePreference(A11Y_TEXT_SPACING, value);
  };

  const onHighContrastChange = (checked: boolean) => {
    updatePrefs({ highContrast: checked });
    savePreference(A11Y_HIGH_CONTRAST, checked);
  };

  const onUnderlineLinksChange = (checked: boolean) => {
    updatePrefs({ underlineLinks: checked });
    savePreference(A11Y_UNDERLINE_LINKS, checked);
  };

  const onReduceMotionChange = (value: A11yReduceMotion) => {
    updatePrefs({ reduceMotion: value });
    savePreference(A11Y_REDUCE_MOTION, value);
  };

  const onFocusStrongChange = (checked: boolean) => {
    updatePrefs({ focusStrong: checked });
    savePreference(A11Y_FOCUS_STRONG, checked);
  };

  const renderPreferenceSwitch = (
    checked: boolean,
    onChange: (value: boolean) => void,
    ariaLabel: string
  ) => (
    <Space className="a11y-preference-switch">
      <GraySpan>{checked ? t('common.on') : t('common.off')}</GraySpan>
      <Tooltip
        title={
          checked
            ? t('common.toggle-on-click-to-turn-off')
            : t('common.toggle-off-click-to-turn-on')
        }
      >
        <span>
          <Switch
            aria-label={ariaLabel}
            checked={checked}
            className={checked ? 'switch-checked' : ''}
            onChange={onChange}
          />
        </span>
      </Tooltip>
    </Space>
  );

  if (pwaMobileNav) {
    return (
      <React.Fragment>
        <Helmet>
          <title>{`${t('accessibility.settings.page-title')} | ${process.env.PROJECT_NAME}`}</title>
          <meta
            name="description"
            content={t('accessibility.settings.meta-description')}
          />
        </Helmet>
        <PwaAccessibilityPage />
      </React.Fragment>
    );
  }

  return (
    <React.Fragment>
      <Helmet>
        <title>{`${t('accessibility.settings.page-title')} | ${process.env.PROJECT_NAME}`}</title>
        <meta
          name="description"
          content={t('accessibility.settings.meta-description')}
        />
      </Helmet>
      <ProfileWrapper className="accessibility-settings-page">
        <Row>
          <Col
            span={24}
            xl={{ span: 18 }}
            xxl={{ offset: 2, span: 16 }}
          >
            <Title
              level={3}
              className="a11y-page-title"
            >
              {t('accessibility.settings.page-title')}
            </Title>
            <Text
              className="a11y-page-intro"
            >
              {t('accessibility.settings.page-intro')}
            </Text>

            <Space direction="vertical" size={16} className="a11y-settings-stack">
              <Card className="a11y-card a11y-card--flat">
                <div className="a11y-section-title">
                  {t('accessibility.settings.section-text')}
                </div>
                <div className="a11y-section-help">
                  {t('accessibility.settings.section-text-help')}
                </div>
                <div className="a11y-control-row a11y-control-row--radio">
                  <div>
                    <div className="a11y-control-label">
                      {t('accessibility.settings.text-size-label')}
                    </div>
                  </div>
                  <Radio.Group
                    value={a11yPrefs.textScale}
                    onChange={(e) =>
                      onTextScaleChange(e.target.value as A11yTextScale)
                    }
                    optionType="button"
                    buttonStyle="solid"
                  >
                    <Radio.Button value={1}>
                      {t('accessibility.settings.text-size-default')}
                    </Radio.Button>
                    <Radio.Button value={1.125}>
                      {t('accessibility.settings.text-size-large')}
                    </Radio.Button>
                    <Radio.Button value={1.25}>
                      {t('accessibility.settings.text-size-larger')}
                    </Radio.Button>
                  </Radio.Group>
                </div>
                <div className="a11y-control-row a11y-control-row--radio">
                  <div>
                    <div className="a11y-control-label">
                      {t('accessibility.settings.text-spacing-label')}
                    </div>
                  </div>
                  <Radio.Group
                    value={a11yPrefs.textSpacing}
                    onChange={(e) =>
                      onTextSpacingChange(e.target.value as A11yTextSpacing)
                    }
                    optionType="button"
                    buttonStyle="solid"
                  >
                    <Radio.Button value="default">
                      {t('accessibility.settings.text-spacing-default')}
                    </Radio.Button>
                    <Radio.Button value="relaxed">
                      {t('accessibility.settings.text-spacing-relaxed')}
                    </Radio.Button>
                  </Radio.Group>
                </div>
              </Card>

              <Card className="a11y-card a11y-card--flat">
                <div className="a11y-section-title">
                  {t('accessibility.settings.section-contrast')}
                </div>
                <div className="a11y-section-help">
                  {t('accessibility.settings.section-contrast-help')}
                </div>
                <div className="a11y-control-row">
                  <div>
                    <div className="a11y-control-label">
                      {t('accessibility.settings.high-contrast-label')}
                    </div>
                    <div className="a11y-control-help">
                      {t('accessibility.settings.high-contrast-help')}
                    </div>
                  </div>
                  {renderPreferenceSwitch(
                    a11yPrefs.highContrast,
                    onHighContrastChange,
                    t('accessibility.settings.high-contrast-label')
                  )}
                </div>
                <div className="a11y-control-row">
                  <div>
                    <div className="a11y-control-label">
                      {t('accessibility.settings.underline-links-label')}
                    </div>
                    <div className="a11y-control-help">
                      {t('accessibility.settings.underline-links-help')}
                    </div>
                  </div>
                  {renderPreferenceSwitch(
                    a11yPrefs.underlineLinks,
                    onUnderlineLinksChange,
                    t('accessibility.settings.underline-links-label')
                  )}
                </div>
              </Card>

              <Card className="a11y-card a11y-card--flat">
                <div className="a11y-section-title">
                  {t('accessibility.settings.section-motion')}
                </div>
                <div className="a11y-section-help">
                  {t('accessibility.settings.section-motion-help')}
                </div>
                <div className="a11y-control-row a11y-control-row--radio">
                  <div>
                    <div className="a11y-control-label">
                      {t('accessibility.settings.reduce-motion-label')}
                    </div>
                  </div>
                  <Radio.Group
                    value={a11yPrefs.reduceMotion}
                    onChange={(e) =>
                      onReduceMotionChange(
                        e.target.value as A11yReduceMotion
                      )
                    }
                    optionType="button"
                    buttonStyle="solid"
                  >
                    <Radio.Button value="system">
                      {t('accessibility.settings.reduce-motion-system')}
                    </Radio.Button>
                    <Radio.Button value="on">
                      {t('accessibility.settings.reduce-motion-on')}
                    </Radio.Button>
                    <Radio.Button value="off">
                      {t('accessibility.settings.reduce-motion-off')}
                    </Radio.Button>
                  </Radio.Group>
                </div>
              </Card>

              <Card className="a11y-card a11y-card--flat">
                <div className="a11y-section-title">
                  {t('accessibility.settings.section-keyboard')}
                </div>
                <div className="a11y-section-help">
                  {t('accessibility.settings.section-keyboard-help')}
                </div>
                <div className="a11y-control-row">
                  <div>
                    <div className="a11y-control-label">
                      {t('accessibility.settings.focus-strong-label')}
                    </div>
                    <div className="a11y-control-help">
                      {t('accessibility.settings.focus-strong-help')}
                    </div>
                  </div>
                  {renderPreferenceSwitch(
                    a11yPrefs.focusStrong,
                    onFocusStrongChange,
                    t('accessibility.settings.focus-strong-label')
                  )}
                </div>
              </Card>

              <div className="a11y-footer-note">
                <div>{t('accessibility.settings.personal-note')}</div>
                <div className="a11y-footer-subnote">
                  {t('accessibility.settings.zoom-tip')}
                </div>
                <div className="a11y-footer-link-wrap">
                  <Link
                    className="a11y-statement-link"
                    to={routes.accessibilityStatement}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {t('accessibility.settings.statement-link')}
                  </Link>
                </div>
              </div>
            </Space>
          </Col>
        </Row>
      </ProfileWrapper>
    </React.Fragment>
  );
};

export default AccessibilityPage;
